import { useState } from "react";
import type { UserRole } from "../../hooks/useUserAccess";
import { useUserAccess } from "../../hooks/useUserAccess";

const ROLE_BADGE: Record<UserRole, { cls: string; label: string }> = {
  admin:   { cls: "ac-badge ac-badge--maintenance", label: "Admin" },
  user:    { cls: "ac-badge ac-badge--operational", label: "User" },
  pending: { cls: "ac-badge ac-badge--degraded",    label: "Pending" },
};

type FilterRole = "all" | "admin" | "user" | "pending";

export function UserAccessPage() {
  const { users, loading, error, actionLoading, setAccess, setRole, addUser } = useUserAccess();

  const [filterRole, setFilterRole] = useState<FilterRole>("all");

  // Add user dialog state
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  function openAddDialog() {
    setNewEmail("");
    setNewRole("user");
    setAddError(null);
    setShowAdd(true);
  }

  async function handleAddUser(e: React.FormEvent) {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) { setAddError("Email is required."); return; }
    setAdding(true);
    setAddError(null);
    const err = await addUser(email, newRole);
    setAdding(false);
    if (err) { setAddError(err); return; }
    setShowAdd(false);
  }

  const totalCount   = users.length;
  const activeCount  = users.filter(u => u.role !== "pending").length;
  const adminCount   = users.filter(u => u.role === "admin").length;
  const pendingCount = users.filter(u => u.role === "pending").length;

  const filtered = filterRole === "all" ? users : users.filter(u => u.role === filterRole);

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">User Access Management</h1>
          <p className="ac-page-subtitle">
            Manage portal access and roles for Microsoft SSO users.
            <strong> User</strong> role has portal access.
            <strong> Admin</strong> role can also manage users.
          </p>
        </div>
        <div className="ac-page-header__actions">
          <button
            type="button"
            className="aptean-btn aptean-btn--primary"
            onClick={openAddDialog}
          >
            + Add User
          </button>
        </div>
      </div>

      <div className="ac-summary-strip">
        <div className="ac-summary-card">
          <div className="ac-summary-card__value">{totalCount}</div>
          <div className="ac-summary-card__label">Total</div>
        </div>
        <div className={`ac-summary-card${activeCount > 0 ? " ac-summary-card--ok" : ""}`}>
          <div className="ac-summary-card__value">{activeCount}</div>
          <div className="ac-summary-card__label">Active</div>
        </div>
        <div className={`ac-summary-card${adminCount > 0 ? " ac-summary-card--accent" : ""}`}>
          <div className="ac-summary-card__value">{adminCount}</div>
          <div className="ac-summary-card__label">Admins</div>
        </div>
        <div className={`ac-summary-card${pendingCount > 0 ? " ac-summary-card--warn" : ""}`}>
          <div className="ac-summary-card__value">{pendingCount}</div>
          <div className="ac-summary-card__label">Pending</div>
        </div>
      </div>

      <div className="ac-filter-bar">
        <select
          className="aptean-input"
          value={filterRole}
          onChange={e => setFilterRole(e.target.value as FilterRole)}
          aria-label="Filter by role"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {error && <div role="alert" className="ac-alert ac-alert--error">{error}</div>}

      {loading ? (
        <div className="ac-empty">
          <div className="ac-empty__title">Loading...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ac-empty">
          <div className="ac-empty__icon">&#128274;</div>
          <div className="ac-empty__title">
            {filterRole === "all" ? "No SSO users yet" : `No ${filterRole} users`}
          </div>
          <div className="ac-empty__body">
            {filterRole === "all"
              ? "Add users below or wait for them to sign in with Microsoft SSO."
              : "Try a different filter."}
          </div>
        </div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table" aria-label="SSO Users">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
                <th className="ac-td--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => {
                const badge = ROLE_BADGE[user.role];
                const busy = actionLoading === user.id;
                const isProtected = user.email.toLowerCase() === "nagesh.hugar@aptean.com";
                return (
                  <tr key={user.id}>
                    <td>
                      {user.display_name || (
                        <span className="ac-td--muted" style={{ fontStyle: "italic" }}>
                          Awaiting first login
                        </span>
                      )}
                    </td>
                    <td className="ac-td--muted">{user.email}</td>
                    <td>
                      <span className={badge.cls}>{badge.label}</span>
                    </td>
                    <td className="ac-td--muted">
                      {user.last_sso_login
                        ? new Date(user.last_sso_login).toLocaleString()
                        : "Never"}
                    </td>
                    <td className="ac-td--actions">
                      {user.role === "pending" ? (
                        <button
                          className="aptean-btn aptean-btn--primary aptean-btn--sm"
                          disabled={busy}
                          onClick={() => setAccess(user.id, true)}
                        >
                          {busy ? "..." : "Grant Access"}
                        </button>
                      ) : isProtected ? (
                        <span
                          className="ac-td--muted"
                          style={{ fontSize: "0.8125rem" }}
                          title="Default admin — role and access cannot be changed"
                        >
                          &#128274; Protected
                        </span>
                      ) : (
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <select
                            className="aptean-input"
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.8125rem", height: "2rem" }}
                            value={user.role}
                            disabled={busy}
                            onChange={(e) =>
                              setRole(user.id, e.target.value as "user" | "admin")
                            }
                            aria-label={`Role for ${user.display_name || user.email}`}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            className="aptean-btn aptean-btn--ghost aptean-btn--sm"
                            disabled={busy}
                            onClick={() => setAccess(user.id, false)}
                            title="Revoke portal access"
                          >
                            {busy ? "..." : "Revoke"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User dialog */}
      {showAdd && (
        <div
          className="ac-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}
        >
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Add User</h2>
              <button
                type="button"
                className="ac-dialog__close"
                onClick={() => setShowAdd(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="ac-dialog__body">
                <p style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)", marginBottom: "0.25rem" }}>
                  The user will be pre-registered with the selected role. They will be able to sign
                  in immediately using their Microsoft account.
                </p>
                {addError && (
                  <div className="ac-alert ac-alert--error">{addError}</div>
                )}
                <div className="ac-field">
                  <label htmlFor="add-email">Email address</label>
                  <input
                    id="add-email"
                    type="email"
                    className="aptean-input"
                    placeholder="user@aptean.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="add-role">Role</label>
                  <select
                    id="add-role"
                    className="aptean-input"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as "user" | "admin")}
                  >
                    <option value="user">User — portal access only</option>
                    <option value="admin">Admin — can manage users</option>
                  </select>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button
                  type="button"
                  className="aptean-btn aptean-btn--ghost"
                  onClick={() => setShowAdd(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aptean-btn aptean-btn--primary"
                  disabled={adding}
                >
                  {adding ? "Adding..." : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
